import React, { useState, useRef, useEffect } from 'react';
import { documentsAPI } from '../api';

const DOC_TYPE_ICONS = {
  pdf: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
      <path d="M9 15v-2h1.5a1.5 1.5 0 0 1 0 3H9z" /><path d="M15 11v6" />
    </svg>
  ),
  jpg: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  link: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
};

const ICON_BTN = {
  pencil: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  ),
  download: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  external: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
  trash: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
    </svg>
  ),
};

const getDocIcon = (doc) => {
  if (doc.type === 'link') return DOC_TYPE_ICONS.link;
  if (doc.file_type === 'pdf') return DOC_TYPE_ICONS.pdf;
  return DOC_TYPE_ICONS.jpg;
};

const iconBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '6px',
  color: 'var(--kahla-taupe)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'color 0.15s ease, background-color 0.15s ease',
  borderRadius: '4px',
};

const DocumentsSection = ({ documents, boardId, isExpired, onDocumentsChanged, t = {} }) => {
  const [uploading, setUploading] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [pendingAutoEditId, setPendingAutoEditId] = useState(null);
  const fileInputRef = useRef(null);
  const editInputRef = useRef(null);

  // When the parent refetches and the freshly-uploaded doc shows up, jump
  // straight into rename mode for it.
  useEffect(() => {
    if (!pendingAutoEditId) return;
    const exists = documents.some((d) => d.id === pendingAutoEditId);
    if (exists) {
      const doc = documents.find((d) => d.id === pendingAutoEditId);
      setEditingId(pendingAutoEditId);
      setEditingValue(doc.title || '');
      setPendingAutoEditId(null);
    }
  }, [documents, pendingAutoEditId]);

  // Focus + select the input each time we enter edit mode
  useEffect(() => {
    if (editingId && editInputRef.current) {
      const el = editInputRef.current;
      el.focus();
      el.select();
    }
  }, [editingId]);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setError('');
    setUploading(true);

    let lastUploadedId = null;
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await documentsAPI.uploadFile(boardId, formData);
        lastUploadedId = res?.data?.id || lastUploadedId;
      }
      await onDocumentsChanged();
      if (lastUploadedId) setPendingAutoEditId(lastUploadedId);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed';
      setError(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddLink = async () => {
    if (!linkUrl.trim()) return;
    setError('');
    setLinkSubmitting(true);
    try {
      await documentsAPI.addLink(boardId, linkUrl.trim(), linkTitle.trim() || null);
      setLinkUrl('');
      setLinkTitle('');
      setShowLinkForm(false);
      onDocumentsChanged();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add link');
    } finally {
      setLinkSubmitting(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await documentsAPI.delete(boardId, docId);
      setDeleteConfirmId(null);
      onDocumentsChanged();
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const startRename = (doc) => {
    setEditingId(doc.id);
    setEditingValue(doc.title || '');
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingValue('');
  };

  const saveRename = async (doc) => {
    const next = (editingValue || '').trim();
    if (!next || next === doc.title) {
      cancelRename();
      return;
    }
    try {
      await documentsAPI.rename(boardId, doc.id, next);
      setEditingId(null);
      setEditingValue('');
      onDocumentsChanged();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to rename');
      cancelRename();
    }
  };

  const openDocument = (doc) => {
    if (doc.type === 'link') {
      window.open(doc.url, '_blank', 'noopener,noreferrer');
    } else {
      // Use the current page origin (e.g. maestra.space on production)
      // instead of the preview-aliased REACT_APP_BACKEND_URL so the
      // photographer's own preview tab also stays on the studio's
      // branded host.
      window.open(`${window.location.origin}${doc.file_url}`, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div data-testid="documents-section" style={{
      marginTop: '48px',
      paddingTop: '32px',
      borderTop: '1px solid var(--kahla-border)',
    }}>
      <h2 style={{
        fontFamily: 'Playfair Display, serif',
        fontSize: '24px',
        color: 'var(--kahla-charcoal)',
        marginBottom: '8px',
      }}>
        {t.documents_title || 'Documents'}
      </h2>
      <p style={{
        fontSize: '13px',
        color: 'var(--kahla-text-light)',
        marginBottom: '24px',
      }}>
        {t.attach_project_materials || t.documents_subtitle || 'Attach project materials — offers, briefs, references, or terms'}
      </p>

      {error && (
        <div data-testid="doc-error-message" style={{
          backgroundColor: '#fef3f2',
          border: '1px solid #fecaca',
          color: '#991b1b',
          padding: '12px 16px',
          fontSize: '13px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontSize: '16px' }}>×</button>
        </div>
      )}

      {!isExpired && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            multiple
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            data-testid="doc-file-input"
          />
          <button
            className="btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="doc-upload-btn"
            style={{ fontSize: '13px', padding: '8px 16px' }}
          >
            {uploading ? (t.uploading || 'Uploading...') : `+ ${t.upload_file || 'Upload file'}`}
          </button>
          <button
            className="btn-secondary"
            onClick={() => { setShowLinkForm(!showLinkForm); setError(''); }}
            data-testid="doc-add-link-btn"
            style={{ fontSize: '13px', padding: '8px 16px' }}
          >
            + Add link
          </button>
          <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)', alignSelf: 'center' }}>
            PDF up to 10 MB · Images up to 3 MB
          </span>
        </div>
      )}

      {showLinkForm && !isExpired && (
        <div style={{
          backgroundColor: 'var(--kahla-cream-light)',
          border: '1px solid var(--kahla-border)',
          padding: '16px',
          marginBottom: '20px',
        }}>
          <div style={{ marginBottom: '10px' }}>
            <input
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://..."
              data-testid="doc-link-url-input"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--kahla-border)',
                backgroundColor: 'var(--kahla-white)',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
              }}
            />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <input
              value={linkTitle}
              onChange={(e) => setLinkTitle(e.target.value)}
              placeholder="Title (optional)"
              data-testid="doc-link-title-input"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--kahla-border)',
                backgroundColor: 'var(--kahla-white)',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              className="btn-secondary"
              onClick={() => { setShowLinkForm(false); setLinkUrl(''); setLinkTitle(''); }}
              style={{ fontSize: '12px', padding: '8px 14px' }}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleAddLink}
              disabled={!linkUrl.trim() || linkSubmitting}
              data-testid="doc-link-submit-btn"
              style={{ fontSize: '12px', padding: '8px 14px' }}
            >
              {linkSubmitting ? 'Adding...' : 'Add link'}
            </button>
          </div>
        </div>
      )}

      {documents.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          {documents.map((doc) => {
            const isEditing = editingId === doc.id;
            const meta = doc.type === 'file'
              ? `${(doc.file_type || '').toUpperCase()} · ${doc.file_size_display}`
              : 'Link';

            return (
              <div
                key={doc.id}
                data-testid={`doc-item-${doc.id}`}
                className="doc-row"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '16px 18px',
                  backgroundColor: 'var(--kahla-white)',
                  border: '1px solid var(--kahla-border)',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--kahla-cream-light)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--kahla-white)'; }}
              >
                <div style={{ color: 'var(--kahla-taupe)', flexShrink: 0 }}>
                  {getDocIcon(doc)}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  {isEditing ? (
                    <input
                      ref={editInputRef}
                      data-testid={`doc-title-input-${doc.id}`}
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onBlur={() => saveRename(doc)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); saveRename(doc); }
                        if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                      }}
                      maxLength={200}
                      style={{
                        width: '100%',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        color: 'var(--kahla-charcoal)',
                        padding: '4px 8px',
                        border: '1px solid var(--kahla-border)',
                        background: 'var(--kahla-white)',
                        outline: 'none',
                      }}
                    />
                  ) : (
                    <div
                      data-testid={`doc-title-${doc.id}`}
                      onClick={() => !isExpired && startRename(doc)}
                      title={isExpired ? '' : 'Click to rename'}
                      style={{
                        fontSize: '14px',
                        color: 'var(--kahla-charcoal)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: isExpired ? 'default' : 'text',
                      }}
                    >
                      {doc.title || 'Untitled'}
                    </div>
                  )}
                  <div style={{
                    fontSize: '11px',
                    color: 'var(--kahla-text-light)',
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap',
                    letterSpacing: '0.04em',
                  }}>
                    <span>{meta}</span>
                    <span style={{ width: '1px', height: '10px', backgroundColor: 'var(--kahla-border)', flexShrink: 0 }} />
                    {doc.confirmed_at ? (
                      <span data-testid={`doc-viewed-status-${doc.id}`} style={{ color: '#15803d', fontWeight: 500 }}>
                        ✓ Confirmed on {new Date(doc.confirmed_at).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}, {new Date(doc.confirmed_at).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    ) : doc.viewed_at ? (
                      <span data-testid={`doc-viewed-status-${doc.id}`} style={{ color: '#16a34a', fontStyle: 'italic' }}>
                        Viewed on {new Date(doc.viewed_at).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}, {new Date(doc.viewed_at).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    ) : (
                      <span data-testid={`doc-viewed-status-${doc.id}`} style={{ fontStyle: 'italic', opacity: 0.7 }}>
                        Not viewed yet
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '2px', flexShrink: 0, alignItems: 'center' }}>
                  {!isExpired && !isEditing && (
                    <button
                      onClick={() => startRename(doc)}
                      data-testid={`doc-rename-${doc.id}`}
                      title="Rename"
                      aria-label="Rename document"
                      style={iconBtnStyle}
                      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kahla-charcoal)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-taupe)'; }}
                    >
                      {ICON_BTN.pencil}
                    </button>
                  )}
                  <button
                    onClick={() => openDocument(doc)}
                    data-testid={`doc-open-${doc.id}`}
                    title={doc.type === 'file' ? 'Download' : 'Open link'}
                    aria-label={doc.type === 'file' ? 'Download document' : 'Open link'}
                    style={iconBtnStyle}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kahla-charcoal)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-taupe)'; }}
                  >
                    {doc.type === 'file' ? ICON_BTN.download : ICON_BTN.external}
                  </button>
                  {!isExpired && (
                    deleteConfirmId === doc.id ? (
                      <div style={{ display: 'flex', gap: '4px', marginLeft: '4px' }}>
                        <button
                          onClick={() => handleDelete(doc.id)}
                          data-testid={`doc-confirm-delete-${doc.id}`}
                          style={{
                            background: '#dc2626', border: 'none', cursor: 'pointer',
                            padding: '5px 10px', fontSize: '11px', color: 'white',
                          }}
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          style={{
                            background: 'none', border: '1px solid var(--kahla-border)', cursor: 'pointer',
                            padding: '5px 10px', fontSize: '11px', color: 'var(--kahla-taupe)',
                          }}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirmId(doc.id)}
                        data-testid={`doc-delete-${doc.id}`}
                        title="Remove"
                        aria-label="Remove document"
                        style={iconBtnStyle}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#C75050'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-taupe)'; }}
                      >
                        {ICON_BTN.trash}
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p
          data-testid="documents-empty-state"
          style={{ fontSize: '13px', color: 'var(--kahla-text-light)', fontStyle: 'italic' }}
        >
          No documents yet. Add files to share details with your client.
        </p>
      )}
    </div>
  );
};

export default DocumentsSection;
